/**
 * furniture_functions — what a piece of furniture DOES, PURE (2026-09-24).
 *
 * Every furniture key maps to at most one function: storage (a container, owner-only to the
 * bone), sit, lie (a bed), a toggle (lamps, hearths, holo-screens), an opener (the Workbench,
 * the market, the Passport), music (the orchestrion), garden (the planter), guestbook. The
 * container capacities are the SERVER's (server/src/lib/furniture_functions.ts — the harness
 * pins the two tables equal); the seat poses are the client's. A key with no entry is decor.
 */

import { FURNITURE } from './furniture_catalog.js';
export type FurnitureFunction = 'storage' | 'sit' | 'lie' | 'toggle' | 'craft' | 'market' | 'passport' | 'music' | 'garden' | 'guestbook';

/** Container capacities — a MIRROR of the server's CONTAINER_CAPACITY. */
export const CONTAINER_CAPACITY: Record<string, number> = {
  chest_storage: 24, crate_storage: 36, safe_vault: 12, wardrobe_tall: 16, cabinet_low: 12, shelf_unit: 12,
  bookcase_arch: 10, cooler_unit: 12, kitchen_unit: 8, weapon_rack: 4,
  // the interior items (2026-10-01) — see the server table for the reasoning
  locker_bank: 18, parts_rack: 18, wash_unit: 8, service_trolley: 6,
  service_shelf: 16, // round 2
};
/** Containers that take only one kind of item (the rack takes weapons). */
export const CONTAINER_FILTER: Record<string, 'weapon'> = { weapon_rack: 'weapon' };

export const FURNITURE_FUNCTION: Record<string, FurnitureFunction> = {
  // storage
  chest_storage: 'storage', crate_storage: 'storage', safe_vault: 'storage', wardrobe_tall: 'storage', cabinet_low: 'storage',
  shelf_unit: 'storage', bookcase_arch: 'storage', cooler_unit: 'storage', kitchen_unit: 'storage', weapon_rack: 'storage',
  locker_bank: 'storage', parts_rack: 'storage', wash_unit: 'storage', service_trolley: 'storage', service_shelf: 'storage',
  // seats and beds
  sofa_low: 'sit', armchair_wing: 'sit', bench_hall: 'sit', chair_dining: 'sit', pod_lounge: 'sit', stool_shop: 'sit', hover_chair: 'sit',
  seat_row: 'sit',
  bed_double: 'lie', bed_single: 'lie', pod_sleep: 'lie',
  // toggles
  lamp_floor: 'toggle', lamp_table: 'toggle', light_column: 'toggle', stove_hearth: 'toggle', screen_holo: 'toggle',
  // openers
  bench_work: 'craft', fabricator_pod: 'craft', desk_terminal: 'market', mirror_tall: 'passport',
  process_machine: 'craft', // round 2: it stands where a works' fabricator stood (work.line) and opens the same bench
  // the rest
  orchestrion: 'music', planter_hydro: 'garden', guestbook_pedestal: 'guestbook',
};

export function furnitureFunction(key: string): FurnitureFunction | null { return FURNITURE_FUNCTION[key] ?? null; }

/**
 * DISPLAY PIECES (2026-10-01, the interior items): a plinth and a case SHOW what stands on or in them — a kit seats an
 * item there and hangs a `display` anchor off the piece (interior_kits.ts), a venue reads that anchor. It is recorded
 * here, in its own table, and deliberately NOT a `FurnitureFunction`: every entry of `FURNITURE_FUNCTION` becomes an E
 * claim in FurnitureUseModule (ONE KEY, ONE FOCUS — the nearest claimant takes the key), and a display has no act of
 * its own yet. A plinth in a shop window stands inside the door's 4.5 m: a function that does nothing would take E
 * from the door. When a display gains an act (stock a case, browse a plinth), it becomes a function then.
 * Round 2: the retail shelf and the retail island are displays too — their goods are MODELLED on them (nothing is
 * seated on top), and a shop's `display` anchors hang off them.
 */
export const FURNITURE_DISPLAY: ReadonlySet<string> = new Set(['display_plinth', 'exhibit_case', 'display_shelf', 'display_table']);
export function isDisplayPiece(key: string): boolean { return FURNITURE_DISPLAY.has(key); }

/** How near a player must stand to use a piece (m). */
export const FURNITURE_USE_M = 2.2;

/** What the prompt says for a function. */
export function useLabel(fn: FurnitureFunction, name: string, extra?: { on?: boolean; ready?: boolean; planted?: boolean }): string {
  switch (fn) {
    case 'storage': return `E — open ${name}`;
    case 'sit': return `E — sit on the ${name}`;
    case 'lie': return `E — lie on the ${name}`;
    case 'toggle': return `E — turn the ${name} ${extra?.on ? 'off' : 'on'}`;
    case 'craft': return `E — craft at the ${name}`;
    case 'market': return `E — use the ${name} (market)`;
    case 'passport': return `E — look in the ${name} (Passport)`;
    case 'music': return `E — ${extra?.on ? 'change the tune on' : 'play'} the ${name}`;
    case 'garden': return extra?.ready ? `E — harvest the ${name}` : extra?.planted ? `${name} — growing` : `E — plant in the ${name}`;
    case 'guestbook': return `E — sign the ${name}`;
  }
}

/** The seated pose over a piece: eye height and how far the body sits back from the piece's origin. */
export function seatPose(fn: 'sit' | 'lie', pieceHeight: number): { eye: number; bodyScaleY: number; back: number } {
  if (fn === 'lie') return { eye: Math.max(0.45, Math.min(0.8, pieceHeight * 0.75)), bodyScaleY: 0.32, back: 0 };
  // A seat's cushion sits at ~45 % of the piece; the eye a metre over it. The body crouches to read as seated.
  return { eye: Math.max(0.9, Math.min(1.25, pieceHeight * 0.45 + 0.75)), bodyScaleY: 0.72, back: 0.05 };
}

/** A container key for a crafted piece (a structure_item row) or a furnish-pack piece. */
export function containerKeyFor(piece: { itemId?: bigint | string | null; floor?: number; index?: number; key: string }): string {
  if (piece.itemId !== undefined && piece.itemId !== null) return `item:${piece.itemId}`;
  return `pack:${piece.floor ?? 0}:${piece.index ?? 0}:${piece.key}`;
}

/** The planter's grow time (mirror of the server's PLANTER_GROW_MICROS, in ms). */
export const PLANTER_GROW_MS = 4 * 3600 * 1000;
/** What a harvest yields for one unit planted (the server's PLANTER_YIELD). */
export const PLANTER_YIELD = 3;

/** How near a piece's CENTRE the last-metre walk must get before the avatar sits. The walk ends at the piece's
 *  COLLIDER, not its centre — a chair's is small, a bed's holds the capsule over a metre out — so the reach
 *  grows with the drawn footprint (furnitureFit 1.5 → 0.75·w, 0.75·d) over a base the capsule needs. */
export const SEAT_REACH_BASE_M = 0.5;
export function seatReach(key: string): number {
  const f = FURNITURE[key];
  return SEAT_REACH_BASE_M + (f ? Math.max(f.w, f.d) * 0.75 : 0.5);
}
